import { type FC, useState } from 'react'
import { Checkbox, Collapse, Link } from '@egw/components'
import { always } from '@egw/auth'

export interface DisclosureLink {
  text: string
  url: string
}

interface Props {
  alwaysExpanded?: boolean
  checkboxHelperText: string
  checkboxLabel: string
  expandedLinks?: DisclosureLink[]
  expandedText?: string
  hasBorder?: boolean
  // eslint-disable-next-line
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  testId: string
}

/**
 * Disclosure section expandable box
 * @param alwaysExpanded - starts expanded and chevron is gone
 * @param checkboxHelperText - Text in grey appearing under Disclosure checkbox
 * @param checkboxLabel - Checkbox label
 * @param expandedLinks - Expanded links
 * @param expandedText - Expanded text
 * @param hasBorder - If it has border, add bottom border
 * @param onCheckboxChange - Event when checkbox is clicked
 * @param testId - Test id
 * @returns React.FC
 */

export const DisclosureSection: FC<Props> = ({
  alwaysExpanded,
  checkboxHelperText,
  checkboxLabel,
  expandedLinks,
  expandedText,
  hasBorder = true,
  onCheckboxChange,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    // Wrapper
    <div className={`flex flex-col w-full py-md ${hasBorder ? 'border-light border-b' : ''}`} data-testid={testId}>
      {/* Checkbox */}
      <Checkbox aria-label={checkboxLabel} onChange={onCheckboxChange} testId={`${testId}-checkbox`}>
        <button
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
          className="w-full text-text-header typo-primary-base-semibold-link focus:outline-none"
          type="button"
        />
        <div className="text-text-header typo-primary-base" data-testid={`${testId}-checkbox-label`}>
          {checkboxLabel}
        </div>
        {/* Accordion */}
        <Collapse
          title={checkboxHelperText}
          testId={testId}
          className="typo-primary-base-regular text-text-meta text-left"
          chevronClassName="relative -top-md"
          alwaysExpanded={alwaysExpanded}
        >
          <div className="flex flex-col justify-start text-text-meta typo-primary-base-regular rounded-md transition-all duration-300 ease-in-out">
            {/* Text */}
            {(expandedText && <span data-testid={`${testId}-expanded-text`}>{expandedText}</span>) ?? null}
            {/* Links */}
            {expandedLinks?.map((e, index) => {
              return (
                <Link
                  href={e.url}
                  key={e.text.replaceAll(' ', '-').toLowerCase()}
                  className="mt-sm typo-primary-base-regular"
                  testId={`${testId}-link-${index + 1}`}
                >
                  {e.text}
                </Link>
              )
            })}
          </div>
        </Collapse>
      </Checkbox>
    </div>
  )
}
